import { ChangeDetectionStrategy, Component, computed, EventEmitter, inject, Output } from '@angular/core';
import { SelectItem, SelectMenuComponent, SelectMode } from '../../../shared/components/select-menu/select-menu.component';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { AssetVersionPipe } from '../../../pipes/asset-version.pipe';
import { POLARIS_NAME } from '../copilot.model';
import { tooltips } from '../../../utils/tooltips';
import { DashboardService } from '../../../services/dashboard.service';
import { StateService } from '../../../services/state.service';
import { UtilService } from '../../../services/util.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BehaviorSubject, catchError, map, of, startWith, switchMap } from 'rxjs';
import {StrategyDeckService} from './strategy-deck.service';
import { DeckResearchQuestionComponent } from './components/deck-research-question/deck-research-question.component';
import {
  DeckQuestion,
  DeckQuestionsPayload,
  DeckQuestionTopic,
  DeckQuestionTopicDisplay,
  MAX_QUESTIONS,
  OwnQuestionPayload
} from './strategy-deck.models';
const MAX_COMPETITORS = 5;
const OTHER_COUNTRY_KEY = 'other';

const NO_QUESTIONS: DeckQuestionsPayload = { questions: {}, total: 0 };

/** Loading, empty and failed all rendered as the same blank area before; keep them distinct. */
type DeckQuestionsState =
  | { status: 'loading' }
  | { status: 'loaded'; payload: DeckQuestionsPayload }
  | { status: 'error' };

const LOADING_QUESTIONS: DeckQuestionsState = { status: 'loading' };

/** Rough shape of the real list, so the skeleton reserves a believable amount of space. */
const SKELETON_GROUP_SIZES = [4, 3, 4];

/** A preset question and a user's own question are worth the same; both count here. */
function totalQuestions(group: AbstractControl): number {
  const questions = group.get('questions')?.value ?? [];
  const ownQuestion = group.get('ownQuestion')?.value ?? [];
  return questions.length + ownQuestion.length;
}

function minTotalQuestionsValidator(): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null =>
    totalQuestions(group) === 0 ? { noQuestionsSelected: true } : null;
}

function maxTotalQuestionsValidator(max: number): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null =>
    totalQuestions(group) > max ? { maxQuestionsExceeded: true } : null;
}

export interface StrategyDeckSubmitEvent {
  brandKey: string;
  competitorKeys: string[];
  questions: DeckQuestion[];
  ownQuestion?: OwnQuestionPayload[];
  countries: string[];
  accountKey: string;
}

@Component({
  selector: 'app-strategy-deck',
  imports: [
    ReactiveFormsModule,
    SelectMenuComponent,
    AssetVersionPipe,
    MatTooltipModule,
    DeckResearchQuestionComponent,
  ],
  templateUrl: './strategy-deck.component.html',
  styleUrl: './strategy-deck.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StrategyDeckComponent {

  @Output() exitWizard = new EventEmitter<void>();
  @Output() submitWizard = new EventEmitter<StrategyDeckSubmitEvent>();

  private dashboardService = inject(DashboardService);
  private stateService = inject(StateService);
  private utilService = inject(UtilService);
  private strategyDeckService = inject(StrategyDeckService);

  protected readonly polarisName = POLARIS_NAME;
  protected readonly tooltips = tooltips.polaris;
  protected readonly SelectMode = SelectMode;

  protected readonly topicDisplay = DeckQuestionTopicDisplay;

  private reloadQuestions$ = new BehaviorSubject<void>(undefined);

  /**
   * catchError sits inside the switchMap so a failed request produces an error state
   * rather than killing the stream — otherwise Retry would have nothing to re-run.
   * It also stops toSignal from rethrowing the error while the template reads it.
   */
  private deckQuestionsState = toSignal(
    this.reloadQuestions$.pipe(
      switchMap(() => this.strategyDeckService.getDeckQuestions().pipe(
        map((payload): DeckQuestionsState => ({ status: 'loaded', payload })),
        catchError(() => of<DeckQuestionsState>({ status: 'error' })),
        startWith(LOADING_QUESTIONS),
      )),
    ),
    { initialValue: LOADING_QUESTIONS },
  );

  protected isLoadingQuestions = computed(() => this.deckQuestionsState().status === 'loading');
  protected hasQuestionsError = computed(() => this.deckQuestionsState().status === 'error');

  protected deckQuestions = computed<DeckQuestionsPayload>(() => {
    const state = this.deckQuestionsState();
    return state.status === 'loaded' ? state.payload : NO_QUESTIONS;
  });
  protected deckQuestionTopics = computed(() => Object.keys(this.deckQuestions().questions) as DeckQuestionTopic[]);

  /** A successful response with nothing in it — distinct from still loading. */
  protected hasNoQuestionsAvailable = computed(() =>
    this.deckQuestionsState().status === 'loaded' && this.deckQuestionTopics().length === 0);

  protected readonly skeletonGroups = SKELETON_GROUP_SIZES.map(size => Array.from({ length: size }, (_, i) => i));

  protected readonly MAX_QUESTIONS = MAX_QUESTIONS;

  protected wizardForm: FormGroup = new FormGroup({
    brands: new FormControl<string | null>(null, [Validators.required]),
    competitors: new FormControl<string[]>([], [Validators.required, Validators.minLength(1), Validators.maxLength(MAX_COMPETITORS)]),
    // "At least one" is enforced on the group, not here: an own question satisfies it just as well.
    questions: new FormControl<DeckQuestion[]>([]),
    ownQuestion: new FormControl<OwnQuestionPayload[]>([]),
    countries: new FormControl<string[]>([]),
    dateRange: new FormControl<string | null>(null),
  }, { validators: [minTotalQuestionsValidator(), maxTotalQuestionsValidator(MAX_QUESTIONS)] });

  protected brandControlValue = toSignal(this.wizardForm.get('brands')!.valueChanges, { initialValue: this.wizardForm.get('brands')!.value });

  private questionsValue = toSignal(this.questionsControl.valueChanges, { initialValue: this.questionsControl.value });
  private ownQuestionsValue = toSignal(this.ownQuestionControl.valueChanges, { initialValue: this.ownQuestionControl.value });

  /** Presets and the user's own questions share the same budget of MAX_QUESTIONS. */
  protected selectedQuestionCount = computed(() => this.questionsValue().length + this.ownQuestionsValue().length);
  protected isQuestionLimitReached = computed(() => this.selectedQuestionCount() >= MAX_QUESTIONS);

  protected dateRangeOptions = computed<SelectItem[]>(() => [ { label: 'Last 30 days', key: 'last_30_days' } ]);
  protected brandOptions = computed<SelectItem[]>(() => this.dashboardService.myBrandOptions() || []);
  protected competitorOptions = computed<SelectItem[]>(() => {
    const brandKey = this.brandControlValue();
    return brandKey
      ? this.dashboardService.getAllCompetitorsByBrandKey(brandKey) ?? []
      : [];
  });

  protected countriesOptions = computed<SelectItem[]>(() =>
    this.dashboardService.countriesOptions().filter(country => country.key !== OTHER_COUNTRY_KEY));

  protected showSelectAllOption = computed<boolean>(() => this.competitorOptions().length <= MAX_COMPETITORS);

  protected get brandsControl(): FormControl<string | null> {
    return this.wizardForm.get('brands') as FormControl<string | null>;
  }

  protected get competitorsControl(): FormControl<string[]> {
    return this.wizardForm.get('competitors') as FormControl<string[]>;
  }

  protected get questionsControl(): FormControl<DeckQuestion[]> {
    return this.wizardForm.get('questions') as FormControl<DeckQuestion[]>;
  }

  protected get ownQuestionControl(): FormControl<OwnQuestionPayload[]> {
    return this.wizardForm.get('ownQuestion') as FormControl<OwnQuestionPayload[]>;
  }

  protected get countriesControl(): FormControl<string[]> {
    return this.wizardForm.get('countries') as FormControl<string[]>;
  }

  protected get dateRangeControl(): FormControl<string | null> {
    return this.wizardForm.get('dateRange') as FormControl<string | null>;
  }

  protected get isFormValid(): boolean {
    return this.wizardForm.valid;
  }

  protected get hasTooManyQuestions(): boolean {
    return !!this.wizardForm.errors?.['maxQuestionsExceeded'];
  }

  protected get hasNoQuestions(): boolean {
    return !!this.wizardForm.errors?.['noQuestionsSelected'];
  }

  protected isQuestionSelected(question: DeckQuestion): boolean {
    return this.questionsValue().some(q => q.id === question.id);
  }

  protected isOwnQuestionSelected(topic: DeckQuestionTopic): boolean {
    return this.ownQuestionsValue().some(q => q.topic === topic);
  }

  /** At the limit, everything not already selected locks — deselecting any of them releases the rest. */
  protected isQuestionLocked(question: DeckQuestion): boolean {
    return this.isQuestionLimitReached() && !this.isQuestionSelected(question);
  }

  protected isOwnQuestionLocked(topic: DeckQuestionTopic): boolean {
    return this.isQuestionLimitReached() && !this.isOwnQuestionSelected(topic);
  }

  protected onQuestionToggle(question: DeckQuestion, isChecked: boolean): void {
    const current = this.questionsControl.value;
    const updated = isChecked
      ? [...current, question]
      : current.filter(q => q.id !== question.id);
    this.questionsControl.setValue(updated);
  }

  /** Typing an own question selects it; emptying the text deselects it. */
  protected onOwnQuestionTextChange(topic: DeckQuestionTopic, text: string): void {
    this.setOwnQuestion(topic, text);
  }

  /**
   * Unticking drops the question but leaves the text, so it can be re-selected.
   * Ticking an empty row has nothing to select, so send the user to the input instead.
   */
  protected onOwnQuestionToggle(topic: DeckQuestionTopic, checked: boolean, input: HTMLInputElement): void {
    this.setOwnQuestion(topic, checked ? input.value : '');
    if (checked && !input.value.trim()) {
      input.focus();
    }
  }

  private setOwnQuestion(topic: DeckQuestionTopic, text: string): void {
    const question = text.trim();
    const withoutTopic = this.ownQuestionControl.value.filter(q => q.topic !== topic);
    this.ownQuestionControl.setValue(question ? [...withoutTopic, { question, topic }] : withoutTopic);
  }

  protected submit(): void {
    if (this.hasTooManyQuestions) {
      this.utilService.openSnackBar(`You can select up to ${MAX_QUESTIONS} questions in total`, { variant: 'error' });
      return;
    }

    if (this.hasNoQuestions) {
      this.utilService.openSnackBar('Please select a research question, or add your own', { variant: 'error' });
      return;
    }

    if (!this.isFormValid) {
      this.utilService.openSnackBar('Please select a brand and at least one competitor', { variant: 'error' });
      return;
    }

    const accountKey = this.stateService.activeAccount();
    this.submitWizard.emit({
      brandKey: this.brandsControl.value!,
      competitorKeys: this.competitorsControl.value,
      questions: this.questionsControl.value,
      ownQuestion: this.ownQuestionControl.value,
      countries: this.countriesControl.value,
      accountKey,
    });
  }

  protected retryLoadQuestions(): void {
    this.reloadQuestions$.next();
  }

  protected exit(): void {
    this.exitWizard.emit();
  }

}
